import { useCallback, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styles from './FunVizTabs.module.css';

// Tab order matters for Home/End and wraparound Arrow navigation below.
const TABS = [
  { id: 'analysis', label: 'Analysis', path: '/analysis' },
  { id: 'screener', label: 'Screener', path: '/screener' },
  { id: 'markethealth', label: 'Market Health', path: '/markethealth' },
];

const TABPANEL_ID = 'funviz-tabpanel';

// The shell for the /analysis and /screener routes. It owns the page-level
// container and an accessible tab bar that sits ABOVE the tab content — the tab bar
// is its own standalone strip, not chrome wrapped around the content, so
// the active child route (Analysis or Screener) renders its own separate
// white cards below it (Analysis restores its pre-split multi-card layout;
// Screener supplies its own card). This is the "tabs above the first card"
// arrangement, as opposed to the earlier single-card-with-tabs-inside shell.
const FunVizTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabRefs = useRef([]);

  const normalizedPath = location.pathname.replace(/\/+$/, '');
  const activeIndex = normalizedPath.endsWith('/markethealth') ? 2
    : normalizedPath.endsWith('/screener') ? 1
    : 0;

  // Roving-tabindex arrow-key navigation across the tablist, per the WAI-ARIA
  // Tabs pattern: ArrowLeft/ArrowRight move focus with wraparound, Home/End
  // jump to the first/last tab. Moving focus also activates the tab (these
  // are navigation tabs backed by real routes, so there is no separate
  // "select" step).
  const handleKeyDown = useCallback((event, index) => {
    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % TABS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + TABS.length) % TABS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TABS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    tabRefs.current[nextIndex]?.focus();
    navigate(TABS[nextIndex].path);
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div role="tablist" aria-label="FunViz sections" className={styles.tabList}>
        {TABS.map((tab, index) => {
          const selected = index === activeIndex;
          return (
            <Link
              key={tab.id}
              id={`funviz-tab-${tab.id}`}
              to={tab.path}
              role="tab"
              aria-selected={selected}
              aria-controls={TABPANEL_ID}
              tabIndex={selected ? 0 : -1}
              ref={(el) => { tabRefs.current[index] = el; }}
              className={selected ? `${styles.tab} ${styles.tabActive}` : styles.tab}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>
      <div
        id={TABPANEL_ID}
        role="tabpanel"
        aria-labelledby={`funviz-tab-${TABS[activeIndex].id}`}
        className={styles.tabPanel}
      >
        <Outlet />
      </div>
    </div>
  );
};

export default FunVizTabs;
